import { jsPDF } from "jspdf";

// ── Page constants (US Letter, mm) ─────────────────────────
const PAGE_W = 215.9;
const PAGE_H = 279.4;
const MARGIN = 7.6;                       // measured from the original report
const CONTENT_W = PAGE_W - MARGIN * 2;
const RIGHT_EDGE = PAGE_W - MARGIN;
const FOOTER_H = 12;

// ── Results-table column geometry (absolute mm, measured from original) ──
const DIV1 = 91.6;        // Laboratory Test | Result divider
const DIV2 = 153.9;       // Result | Reference Range divider
const DIV3 = 197.2;       // Reference Range | flag divider
const NAME_X = MARGIN + 5;   // data test-name indent
const VALUE_X = DIV1 + 2;    // data result value (left-aligned)
const RANGE_X = DIV2 + 2;    // data reference range (left-aligned)
const H_TEST = (MARGIN + DIV1) / 2;   // header label centers
const H_RESULT = (DIV1 + DIV2) / 2;
const H_RANGE = (DIV2 + DIV3) / 2;
const ROW_H = 5.0;

// ── Colors (sampled from the original report) ──────────────
const HEADER_FILL = [230, 230, 230];  // column-header gray fill
const SECTION_BLUE = [49, 148, 197];  // section (department) band
const GRIDLINE = [224, 224, 224];     // horizontal row rules
const BORDER = [140, 140, 140];       // column-header box border
const ABNORMAL_RED = [222, 0, 0];     // abnormal result values

// ── Static lab identity (matches the stored report) ────────
const LAB = {
  title: "Laboratory Report",
  name: "LINCOLN REFERENCE LABORATORY",
  address: "1023 COMMERCE AVENUE, UNION, NJ 07083",
  tel: "Tel: 908-583-5421   Fax: 908-845-0253",
  director: "Director: ROBERT BOORSTEIN, M.D",
  clia: "CLIA# 31D2118440",
  phone: "908-583-5421",
};

// ── Low-level helpers ──────────────────────────────────────
function setFont(doc, size, style = "normal", color = [0, 0, 0]) {
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  doc.setTextColor(color[0], color[1], color[2]);
}

function line(doc, x1, y1, x2, y2, w = 0.2, color = [0, 0, 0]) {
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(w);
  doc.line(x1, y1, x2, y2);
}

function band(doc, x, y, w, h, fill) {
  doc.setFillColor(fill[0], fill[1], fill[2]);
  doc.rect(x, y, w, h, "F");
}

function fitText(doc, text, maxW) {
  if (!text) return "";
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 0 && doc.getTextWidth(t + "...") > maxW) t = t.slice(0, -1);
  return t + "...";
}

// ── Header (drawn on every page) ───────────────────────────
function drawHeader(doc, data, logoBase64) {
  const boxTop = 6;
  const boxH = 30;

  // Banner box around logo + lab identity (as in the original)
  doc.setDrawColor(120, 120, 120);
  doc.setLineWidth(0.3);
  doc.roundedRect(MARGIN, boxTop, CONTENT_W, boxH, 1.5, 1.5);

  // Logo (left, vertically centered)
  if (logoBase64) {
    try { doc.addImage(logoBase64, "PNG", MARGIN + 3, boxTop + 6.5, 42, 16); } catch (e) {}
  }

  // Lab identity (centered)
  const cx = PAGE_W / 2;
  setFont(doc, 15, "normal");
  doc.text(LAB.title, cx, boxTop + 6.5, { align: "center" });
  let ty = boxTop + 10.5;
  // Lab name in bold, the rest of the identity lines normal
  setFont(doc, 6, "bold");
  doc.text(LAB.name, cx, ty, { align: "center" }); ty += 3.0;
  setFont(doc, 6, "normal");
  const labLines = [
    "1023 COMMERCE AVENUE,",
    "UNION, NJ 07083",
    "Tel: 908-583-5421",
    "Fax: 908-845-0253",
    LAB.director,
    LAB.clia,
  ];
  labLines.forEach((t) => { doc.text(t, cx, ty, { align: "center" }); ty += 3.0; });

  let y = boxTop + boxH + 4;
  // "Final Copy"
  setFont(doc, 8.5, "bold");
  doc.text("Final Copy", RIGHT_EDGE, y, { align: "right" });
  y += 4;

  y = drawInfoColumns(doc, data, y);
  return y;
}

// ── Three info columns: Client / Patient / Sample ──────────
function drawInfoColumns(doc, data, y) {
  const { client, physician, patient, sample, accession } = data;
  const col1 = MARGIN;
  const col2 = MARGIN + CONTENT_W * 0.37;
  const col3 = MARGIN + CONTENT_W * 0.70;
  const patValRight = col3 - 3;     // Patient values right-aligned within their column
  const sampValRight = RIGHT_EDGE;  // Sample values right-aligned to the margin
  const lh = 4.2;

  // bold label left, normal value right-aligned (matches the original layout)
  const fieldR = (labelX, valRight, yy, label, value) => {
    setFont(doc, 8.5, "bold");
    doc.text(label, labelX, yy);
    setFont(doc, 8.5, "normal");
    doc.text(value || "", valRight, yy, { align: "right" });
  };

  // Column headings — bold + underlined (as in the original)
  setFont(doc, 8.5, "bold");
  [["Client Information:", col1], ["Patient Information:", col2], ["Sample Information:", col3]]
    .forEach(([label, x]) => {
      doc.text(label, x, y);
      line(doc, x, y + 1, x + doc.getTextWidth(label), y + 1, 0.3, [0, 0, 0]);
    });
  let y1 = y + lh;
  let y2 = y1;
  let y3 = y1;

  // Client (left-aligned)
  setFont(doc, 8.5, "normal");
  doc.text(`${client.name || ""}${client.code ? " (" + client.code + ")" : ""}`, col1, y1); y1 += lh;
  const addr = `${client.address || ""}${client.address2 ? ", " + client.address2 : ""}`.trim();
  if (addr) { doc.text(addr, col1, y1); y1 += lh; }
  doc.text(`${client.city || ""}, ${client.state || ""} ${client.zip || ""}`.trim(), col1, y1); y1 += lh;
  setFont(doc, 8.5, "bold");
  doc.text("Requesting Physician:", col1, y1); y1 += lh;
  setFont(doc, 8.5, "normal");
  doc.text(physician.name || "", col1, y1); y1 += lh;

  // Patient (value right-aligned)
  fieldR(col2, patValRight, y2, "Patient Name:", patient.name); y2 += lh;
  fieldR(col2, patValRight, y2, "Patient ID:", patient.id); y2 += lh;
  fieldR(col2, patValRight, y2, "Date of Birth:", `${patient.dob || ""}${patient.age ? " (" + patient.age + ")" : ""}`); y2 += lh;
  fieldR(col2, patValRight, y2, "Gender:", patient.sex); y2 += lh;
  fieldR(col2, patValRight, y2, "Phone:", patient.phone); y2 += lh;

  // Sample (value right-aligned)
  fieldR(col3, sampValRight, y3, "Lab Sample ID:", accession); y3 += lh;
  fieldR(col3, sampValRight, y3, "Fasting:", sample.fasting); y3 += lh;
  fieldR(col3, sampValRight, y3, "Collected:", sample.collected); y3 += lh;
  fieldR(col3, sampValRight, y3, "Received:", sample.received); y3 += lh;
  fieldR(col3, sampValRight, y3, "Final Reported:", sample.reported); y3 += lh;

  return Math.max(y1, y2, y3) + 1;
}

// ── Column header bar (Laboratory Test | Result | Reference Range)
function drawColumnHeader(doc, y) {
  const h = 5.8;
  band(doc, MARGIN, y, CONTENT_W, h, HEADER_FILL);
  // Box border + vertical column dividers
  doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN, y, CONTENT_W, h);
  [DIV1, DIV2, DIV3].forEach((x) => doc.line(x, y, x, y + h));
  // Centered black labels
  setFont(doc, 10.5, "bold", [0, 0, 0]);
  doc.text("Laboratory Test", H_TEST, y + 4, { align: "center" });
  doc.text("Result", H_RESULT, y + 4, { align: "center" });
  doc.text("Reference Range", H_RANGE, y + 4, { align: "center" });
  // Solid divider line between the bold header and the results (as in the original)
  line(doc, MARGIN, y + h, RIGHT_EDGE, y + h, 0.4, [90, 90, 90]);
  return y + h;
}

// ── Pagination context ─────────────────────────────────────
function makeCtx(doc, data, logoBase64) {
  const ctx = {
    doc, data, logoBase64,
    y: 0,
    startPage() {
      this.y = drawHeader(doc, data, logoBase64);
      this.y = drawColumnHeader(doc, this.y);
      this.y += 1.5;
    },
    ensure(h) {
      if (this.y + h > PAGE_H - FOOTER_H) {
        doc.addPage();
        this.startPage();
      }
    },
  };
  return ctx;
}

// ── Section / panel headers ────────────────────────────────
function drawSectionBand(ctx, label) {
  const { doc } = ctx;
  const h = 5.6;
  ctx.ensure(h + ROW_H * 2);
  band(doc, MARGIN, ctx.y, CONTENT_W, h, SECTION_BLUE);
  setFont(doc, 11, "bold", [255, 255, 255]);
  doc.text(label, MARGIN + 1.5, ctx.y + 4.1);
  ctx.y += h + 0.4;
}

function drawPanelHeader(ctx, label) {
  const { doc } = ctx;
  ctx.ensure(ROW_H * 2);
  setFont(doc, 9.5, "bold", [0, 0, 0]);
  doc.text(label, MARGIN + 1.5, ctx.y + 3.5);
  ctx.y += ROW_H;
  // rule under the panel header, separating it from its results (as in the original)
  line(doc, MARGIN, ctx.y, RIGHT_EDGE, ctx.y, 0.1, GRIDLINE);
}

// ── One result row ─────────────────────────────────────────
function drawResultRow(ctx, r) {
  const { doc } = ctx;
  ctx.ensure(ROW_H);
  const b = ctx.y + 3.4;

  setFont(doc, 9.5, "normal");
  doc.text(fitText(doc, r.name, VALUE_X - NAME_X - 2), NAME_X, b);

  setFont(doc, 9.5, r.abnormal ? "bold" : "normal", r.abnormal ? ABNORMAL_RED : [0, 0, 0]);
  doc.text(fitText(doc, r.value, RANGE_X - VALUE_X - 2), VALUE_X, b);

  setFont(doc, 9.5, "normal", [0, 0, 0]);
  doc.text(fitText(doc, r.range, RIGHT_EDGE - RANGE_X - 1), RANGE_X, b);

  ctx.y += ROW_H;

  // Comment belongs to this result — keep it above the rule
  if (r.comment && r.comment.trim()) drawComment(ctx, r.comment);

  // Thin horizontal rule under the row (and its comment), as in the original
  line(doc, MARGIN, ctx.y, RIGHT_EDGE, ctx.y, 0.1, GRIDLINE);
}

// ── Interpretive comment block (small wrapped text) ────────
function drawComment(ctx, comment) {
  const { doc } = ctx;
  setFont(doc, 7.5, "normal", [50, 50, 50]);
  const indent = MARGIN + 6;
  const maxW = RIGHT_EDGE - indent;
  const rawLines = String(comment).replace(/\r/g, "").split("\n");
  const lh = 3.2;
  rawLines.forEach((raw) => {
    const wrapped = doc.splitTextToSize(raw, maxW);
    wrapped.forEach((ln) => {
      ctx.ensure(lh);
      doc.text(ln, indent, ctx.y + 2.3);
      ctx.y += lh;
    });
  });
  ctx.y += 1.2;
}

// ── Results body (grouped section → panel) ─────────────────
function drawResults(ctx) {
  let curSection = null;
  let curPanel = null;
  ctx.data.results.forEach((r) => {
    if (r.section !== curSection) {
      drawSectionBand(ctx, r.section || "RESULTS");
      curSection = r.section;
      curPanel = null;
    }
    if (r.panel && r.panel !== curPanel) {
      drawPanelHeader(ctx, r.panel);
      curPanel = r.panel;
    }
    drawResultRow(ctx, r);
  });
}

// ── Footer + page numbers (post-pass) ──────────────────────
function drawFooters(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const fy = PAGE_H - 7;
    line(doc, MARGIN, fy - 2, RIGHT_EDGE, fy - 2, 0.2);
    setFont(doc, 7.5, "normal");
    doc.text(LAB.name, MARGIN, fy + 1);
    doc.text(LAB.phone, PAGE_W / 2, fy + 1, { align: "center" });
    doc.text(`Page ${i} of ${total}`, RIGHT_EDGE, fy + 1, { align: "right" });
  }
}

// ── Main export ────────────────────────────────────────────
export function generateReportPDF(data, logoBase64) {
  const doc = new jsPDF({ unit: "mm", format: "letter", orientation: "portrait" });
  const ctx = makeCtx(doc, data, logoBase64);

  ctx.startPage();
  drawResults(ctx);
  drawFooters(doc);

  return doc;
}
